import Page from "../../../layout/Page";
import PageContent from "../../../layout/PageContent";
import PageHeader from "../../../layout/PageHeader";
import * as React from "react";
import {useMemo} from "react";
import {ListTradeInsList} from "../../lists/ListTradeInsList";
import {useParams} from "react-router";
import {ParamBrandHouse} from "../../../Parameters";
import {ViewFilter} from "../../../components/ViewFilter";
import {
   EnumIntegrationConnectorMessages,
   EnumIntegrationInstanceRefType,
   ListsTradeInFragment,
   useBrandTradeInsListPageSelectedTradeInsLazyQuery,
   useBrandTradeInsListPageShopsQuery,
   useTradeInIntegrationExecuteMutation
} from "../../../Queries";
import {RangeCommand} from "../../../components/rangelist/RangeListTypes";
import {SettingsRemote} from "@mui/icons-material";
import {Dialogs} from "../../../DialogProvider";
import {SavingUI} from "../../../SavingProvider";
import {LinearProgress} from "@mui/material";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin} from "../../../atoms/CurrentUser";

export const BrandTradeInsListPagePath = (brandHouseId: string) => {
   return "/brands/" + brandHouseId + "/tradeins"
}

export const BrandTradeInsListPage = () => {
   const {brandHouseId} = useParams<ParamBrandHouse>();
   const [shopIds, setShopIds] = React.useState<string[]>([]);
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);

   // First query to get shops
   const {loading: shopsLoading} = useBrandTradeInsListPageShopsQuery({
      variables: {
         brandHouseId: brandHouseId!,
         instanceFilter: undefined
      },
      skip: !brandHouseId,
      onCompleted: (result: any) => {
         setShopIds(result.ShopMany?.map((shop:any) => shop._id) || []);
      }
   });

   // Build the instance filter once we have shop IDs
   const instanceFilter = useMemo(() => {
      const orConditions = [
         {refType: EnumIntegrationInstanceRefType.LOGISTICSPARTNER}
      ];
      for (const shopId of shopIds) {
         orConditions.push({
            refId: shopId,
            refType: EnumIntegrationInstanceRefType.SHOP
         } as any);
      }
      return {OR: orConditions};
   }, [shopIds]);

   // Second query with the built filter (only after we have shop IDs)
   const {data, loading: instancesLoading} = useBrandTradeInsListPageShopsQuery({
      variables: {
         brandHouseId: brandHouseId!,
         instanceFilter
      },
      skip: !brandHouseId || shopIds.length === 0
   });

   const [executeIntegration] = useTradeInIntegrationExecuteMutation();
   const [fetchSelectedTradeIns] = useBrandTradeInsListPageSelectedTradeInsLazyQuery();

   const extraCommands: RangeCommand<ListsTradeInFragment>[] = useMemo(() => {
      if (!isAdministrator || !data?.IntegrationInstances?.length) {
         return [];
      }

      const integrationCommand: RangeCommand<ListsTradeInFragment> = {
         id: 'integrations',
         title: 'Integrations',
         icon: SettingsRemote,
         children: []
      };

      for (const instance of data.IntegrationInstances || []) {
         const messages = instance.connector?.messages || [];
         if (!messages.includes(EnumIntegrationConnectorMessages.CREATETRADEIN) &&
             !messages.includes(EnumIntegrationConnectorMessages.UPDATETRADEIN)) {
            continue;
         }
         integrationCommand.children!.push({
            id: 'integration_' + instance._id,
            title: 'Send \'UPDATETRADEIN\' to ' + instance.name,
            icon: SettingsRemote,
            execute: async (tradeInIds) => {
               Dialogs.confirm({
                  title: 'Update ' + instance.name + ' for selected trade-ins?',
                  subtitle: 'This will trigger integration for selected trade-ins.'
               }, async () => {
                  await SavingUI.process(async () => {
                     const result = await fetchSelectedTradeIns({
                        variables: {
                           tradeInIds
                        },
                        fetchPolicy: 'no-cache'
                     });
                     const tradeInsById = new Map(
                        (result.data?.TradeInPagination?.items || [])
                           .map((tradeIn) => [String(tradeIn._id), tradeIn])
                     );

                     for (const tradeInId of tradeInIds) {
                        const tradeIn = tradeInsById.get(tradeInId);
                        if (!tradeIn) {
                           throw new Error('Unable to load selected trade-in ' + tradeInId);
                        }

                        await executeIntegration({
                           variables: {
                              refId: tradeIn.shopId,
                              arguments: [tradeIn._id, false, "", instance._id]
                           }
                        });
                     }
                  }, 'Done', 'Updating .. please wait');
               });
            }
         });
      }

      if (integrationCommand.children!.length === 0) {
         return [];
      }

      return [integrationCommand];
   }, [isAdministrator, data, executeIntegration, fetchSelectedTradeIns]);

   if (shopsLoading || instancesLoading) {
      return <Page>
         <PageHeader title="TradeIns"/>
         <PageContent>
            <LinearProgress/>
         </PageContent>
      </Page>;
   }

   return <Page>
      <PageHeader title="TradeIns"/>
      <PageContent>
         <ViewFilter dataType="tradeins">
            {(viewId) => (
               <ListTradeInsList
                  globalId={"brandTradeIns"}
                  extrafilter={{
                     brandHouseId
                  }}
                  extraCommands={extraCommands}
                  viewId={viewId}
               />
            )}
         </ViewFilter>
      </PageContent>
   </Page>
}
